import type { PaginatedResponse } from "@/v2/queries/types";
import { apiClientJSON } from "../apiClient";
import { getUserTagsByLegacyIds } from "../tags/tag-apis";
import {
  ACCOUNT_FILTER_CANDIDATE_SIZE,
  excludeProcessedAndSortNewestFirst,
  isFraudAccountFilter,
  splitClientFilters,
  stripAccountOnlyClientFilters,
} from "./clientFilterParams";

const toCamelCase = (str: string): string =>
  str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };
type JsonObject = { [key: string]: JsonValue };

const transformKeys = <T>(obj: JsonValue): T => {
  if (Array.isArray(obj)) {
    return obj.map(transformKeys) as T;
  }
  if (obj !== null && typeof obj === "object") {
    return Object.keys(obj).reduce<JsonObject>((acc, key) => {
      acc[toCamelCase(key)] = transformKeys((obj as JsonObject)[key]);
      return acc;
    }, {}) as T;
  }
  return obj as T;
};

export interface ClientPlan {
  id: number;
  cityId: number;
  status: string;
  type: string;
  currentPeriodEnd?: string | null;
  canceledAt?: string | null;
  oneTime?: boolean;
  paymentMethodWallet?: boolean;
}

export interface PaymentMethodCard {
  id: string;
  type: "card" | "paypal" | "sepa_debit";
  brand?: string | null;
  last4?: string | null;
  expMonth?: number | null;
  expYear?: number | null;
  payerEmail?: string | null;
}

export interface RecentPayment {
  id: string;
  status: string;
  amountMinor: number;
  currency: string;
  createdUnix: number;
  description?: string | null;
  declineCode?: string | null;
  declineMessage?: string | null;
}

export interface FleetPaymentSummary {
  paymentConfigurationId: string;
  fleetId?: string | null;
  fleetName?: string | null;
  organizationId?: string | null;
  customerId: string;
  customerExists: boolean;
  stripeDashboardUrl?: string | null;
  balanceMinor: number;
  currency?: string | null;
  createdUnix?: number | null;
  defaultPaymentMethodId?: string | null;
  delinquent: boolean;
  paymentMethods: PaymentMethodCard[];
  recentPayments: RecentPayment[];
}

export interface UserPaymentSummary {
  userFound: boolean;
  userId?: string | null;
  legacyUserId?: number | null;
  fleets: FleetPaymentSummary[];
}

export interface Client {
  id: number;
  fullName: string;
  birthDate?: string;
  country?: string;
  city?: string;
  region?: string;
  address?: string;
  postcode?: string;
  email: string;
  emailVerified?: boolean;
  phone?: string;
  whiteLabelId?: number;
  dateCreated?: string;
  status?: string;
  activeCityId?: number;
  stripeProfileUrl?: string;
  paymentSummary?: UserPaymentSummary;
  paymentSummaryUnavailable?: boolean;
  plan?: ClientPlan;
  [key: string]: unknown;
}

const fetchPaymentSummary = async (
  legacyUserId: number
): Promise<UserPaymentSummary> => {
  const response = await apiClientJSON.post(
    "/backend/api/v1/admin/users/payment-summary",
    { legacy_user_id: legacyUserId },
    { timeout: 8000 }
  );
  if (!response.data?.body) {
    throw new Error("Payment summary response missing body");
  }
  return transformKeys<UserPaymentSummary>(response.data.body);
};

export interface ClientListParams {
  page?: number;
  size?: number;
  search?: string;
  sort?: string;
  filter?: string;
  cityId?: number | string;
  whitelabelId?: number | null;
}

export const getClients = async (
  params: ClientListParams
): Promise<PaginatedResponse<Client>> => {
  const { page = 0, size = 25, search = "", sort = "", filter = "" } = params;
  const { accountFilter, paymentFilter, paymentUserIds } =
    splitClientFilters(filter);

  const hasPaymentFilter =
    /(?:^|&)(cityId|planType|planStatus|userStatus|userIds)=/.test(filter);
  const hasAccountFilter = Boolean(accountFilter);

  if (hasPaymentFilter && hasAccountFilter) {
    return getClientsFromCombinedFilters(
      page,
      size,
      search,
      sort,
      accountFilter,
      paymentFilter,
      paymentUserIds
    );
  }

  if (hasPaymentFilter) {
    return getClientsFromPaymentService(
      page,
      size,
      search,
      sort,
      paymentFilter
    );
  }

  let userServiceUrl = `/account-service/dashboard/users?size=${size}&page=${page}`;

  if (search) {
    userServiceUrl += `&search=${encodeURIComponent(search)}`;
  }

  if (sort) {
    userServiceUrl += `&sort=${sort}`;
  }

  if (filter) {
    userServiceUrl += `&${filter}`;
  }

  const usersResponse = await apiClientJSON.get(userServiceUrl);
  const usersData: PaginatedResponse<Client> = usersResponse.data;

  const userIds = (usersData.content || []).map((u) => u.id);
  if (userIds.length === 0) return usersData;

  let paymentUrl = `/payment-service/dashboard/users?size=${userIds.length}&page=0&userIds=${userIds.join(",")}`;
  const enrichmentFilter = stripAccountOnlyClientFilters(filter);

  if (enrichmentFilter) {
    paymentUrl += `&${enrichmentFilter}`;
  }

  try {
    const paymentResponse = await apiClientJSON.get(paymentUrl);
    const paymentData: PaginatedResponse<Client> = paymentResponse.data;
    const planById = new Map((paymentData.content || []).map((u) => [u.id, u]));

    usersData.content = usersData.content.map((user) => {
      const paymentUser = planById.get(user.id);
      if (!paymentUser) return user;
      return {
        ...user,
        plan: paymentUser.plan,
        status: paymentUser.status ?? user.status,
        dateCreated: paymentUser.dateCreated ?? user.dateCreated,
        stripeProfileUrl: paymentUser.stripeProfileUrl,
        activeCityId: paymentUser.activeCityId,
      };
    });
  } catch {
    // Payment service enrichment failed; return users without plan data
  }

  return usersData;
};

const getClientsFromCombinedFilters = async (
  page: number,
  size: number,
  search: string,
  sort: string,
  accountFilter: string,
  paymentFilter: string,
  paymentUserIds: string[]
): Promise<PaginatedResponse<Client>> => {
  let accountUrl = `/account-service/dashboard/users?size=${ACCOUNT_FILTER_CANDIDATE_SIZE}&page=0`;

  if (search) {
    accountUrl += `&search=${encodeURIComponent(search)}`;
  }

  if (sort) {
    accountUrl += `&sort=${sort}`;
  }

  if (accountFilter) {
    accountUrl += `&${accountFilter}`;
  }

  const accountResponse = await apiClientJSON.get(accountUrl);
  const accountData: PaginatedResponse<Client> = accountResponse.data;
  let accountUsers = accountData.content || [];

  if (isFraudAccountFilter(accountFilter)) {
    const tagsByLegacyId = await getUserTagsByLegacyIds(
      accountUsers.map((user) => String(user.id))
    );
    accountUsers = excludeProcessedAndSortNewestFirst(
      accountUsers,
      tagsByLegacyId
    );
  }

  const accountById = new Map(accountUsers.map((u) => [String(u.id), u]));

  let candidateIds = accountUsers.map((u) => String(u.id));
  if (paymentUserIds.length > 0) {
    const paymentUserIdSet = new Set(paymentUserIds);
    candidateIds = candidateIds.filter((id) => paymentUserIdSet.has(id));
  }

  if (candidateIds.length === 0) {
    return {
      content: [],
      totalElements: 0,
      totalPages: 0,
      number: page,
      size,
    };
  }

  // The payment service applies the remaining payment-side filters and must see every candidate to
  // paginate correctly, but candidateIds can hold up to ACCOUNT_FILTER_CANDIDATE_SIZE entries —
  // joining them all into one GET query string blows past the ~8KB URL limit (HTTP 414). Fetch the
  // matches in chunks instead, then paginate client-side over the account-ordered set. The list sort
  // is a fixed id,desc, so candidateIds already carry the final order and need no re-sort.
  const PAYMENT_USER_ID_CHUNK_SIZE = 150;
  const idChunks: string[][] = [];
  for (let i = 0; i < candidateIds.length; i += PAYMENT_USER_ID_CHUNK_SIZE) {
    idChunks.push(candidateIds.slice(i, i + PAYMENT_USER_ID_CHUNK_SIZE));
  }

  const chunkResponses = await Promise.all(
    idChunks.map((ids) => {
      let chunkUrl = `/payment-service/dashboard/users?size=${ids.length}&page=0&userIds=${ids.join(",")}`;
      if (paymentFilter) {
        chunkUrl += `&${paymentFilter}`;
      }
      return apiClientJSON.get(chunkUrl);
    })
  );

  const paymentById = new Map<string, Client>();
  for (const response of chunkResponses) {
    const data: PaginatedResponse<Client> = response.data;
    for (const paymentUser of data.content || []) {
      paymentById.set(String(paymentUser.id), paymentUser);
    }
  }

  // Keep only candidates the payment service matched (the payment-side filter is applied per chunk),
  // preserving the account service's id,desc order, then page over that set client-side.
  const matched = candidateIds
    .filter((id) => paymentById.has(id))
    .map((id) => {
      const paymentUser = paymentById.get(id) as Client;
      const accountUser = accountById.get(id);
      if (!accountUser) return paymentUser;
      return {
        ...accountUser,
        ...paymentUser,
        status: accountUser.status ?? paymentUser.status,
      };
    });

  const start = page * size;
  return {
    content: matched.slice(start, start + size),
    totalElements: matched.length,
    totalPages: Math.ceil(matched.length / size),
    number: page,
    size,
  };
};

const getClientsFromPaymentService = async (
  page: number,
  size: number,
  search: string,
  sort: string,
  filter: string
): Promise<PaginatedResponse<Client>> => {
  let url = `/payment-service/dashboard/users?size=${size}&page=${page}`;

  if (search) {
    url += `&search=${encodeURIComponent(search)}`;
  }

  if (sort) {
    url += `&sort=${sort}`;
  }

  if (filter) {
    url += `&${filter}`;
  }

  const response = await apiClientJSON.get(url);
  const data: PaginatedResponse<Client> = response.data;

  const seen = new Set<number>();
  data.content = (data.content || []).filter((user) => {
    if (seen.has(user.id)) return false;
    seen.add(user.id);
    return true;
  });

  return data;
};

export const getClientById = async (id: number): Promise<Client> => {
  const [paymentResult, accountResult, paymentSummary] =
    await Promise.allSettled([
      apiClientJSON.get(`/payment-service/dashboard/users?userId=${id}&size=1`),
      apiClientJSON.get(
        `/account-service/dashboard/users?search=${id}&size=10`
      ),
      fetchPaymentSummary(id),
    ]);

  const paymentUser =
    paymentResult.status === "fulfilled"
      ? (paymentResult.value.data?.content?.[0] ?? null)
      : null;

  const accountUsers =
    accountResult.status === "fulfilled"
      ? (accountResult.value.data?.content ?? [])
      : [];
  const accountUser = accountUsers.find((u: Client) => u.id === id) ?? null;

  const summary =
    paymentSummary.status === "fulfilled" ? paymentSummary.value : undefined;
  const paymentSummaryUnavailable = paymentSummary.status === "rejected";

  if (!accountUser && !paymentUser) {
    throw new Error(`User ${id} not found`);
  }

  const base = accountUser ?? paymentUser;
  return {
    ...base,
    ...(paymentUser && {
      plan: paymentUser.plan,
      status: paymentUser.status ?? base?.status,
      dateCreated: paymentUser.dateCreated ?? base?.dateCreated,
      stripeProfileUrl: paymentUser.stripeProfileUrl,
      activeCityId: paymentUser.activeCityId,
    }),
    paymentSummary: summary,
    paymentSummaryUnavailable,
    id,
  };
};

export const updateClient = async (
  id: number,
  dto: Partial<Client>
): Promise<Client> => {
  const response = await apiClientJSON.put(
    `/payment-service/dashboard/users/${id}`,
    dto
  );
  return response.data;
};

export const removeClient = async (id: number): Promise<void> => {
  await apiClientJSON.delete(`/payment-service/dashboard/users/${id}`);
};

export const cancelSubscription = async (
  cityId: number,
  userId: number,
  planId: number,
  endDate?: string
): Promise<void> => {
  const url = `/payment-service/dashboard/users/${userId}/cities/${cityId}/plans/${planId}/cancel`;
  const body = endDate ? { endDate } : {};
  await apiClientJSON.post(url, body);
};

export const getPlanTypes = async (): Promise<string[]> => {
  const response = await apiClientJSON.get(
    "/payment-service/dashboard/pricing/plans/statuses"
  );
  return response.data;
};

export const getPlanStatuses = async (): Promise<string[]> => {
  const response = await apiClientJSON.get(
    "/payment-service/dashboard/users/cities/statuses"
  );
  return response.data;
};

export const getClientsFromNewBackend = async (params: {
  legacy_whitelabel_id: string;
  search?: string | null;
  status_filter?: string | null;
  page: number;
  size: number;
}): Promise<PaginatedResponse<Client>> => {
  const response = await apiClientJSON.post("/new-backend-endpoint", params);
  return response.data?.body || response.data;
};

export const getClientFromNewBackend = async (
  userId: number
): Promise<Client | null> => {
  const response = await apiClientJSON.post("/new-backend-user-endpoint", {
    user_id: userId,
  });
  return response.data?.body?.user || response.data?.user || null;
};
